import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  FitnessCenter as FitnessIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchCorporateAnalytics } from '../../redux/corporateEmployeeSlice';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const COLORS = ['#14B8A6', '#F59E0B', '#EF4444', '#8B5CF6', '#10B981', '#3B82F6'];

export const CorporateAnalyticsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { analytics, isLoading, error } = useSelector((state: RootState) => state.corporateEmployee);
  
  const [period, setPeriod] = useState('month');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  const loadAnalytics = useCallback(() => {
    if (period === 'custom' && customDateFrom && customDateTo) {
      dispatch(fetchCorporateAnalytics({ date_from: customDateFrom, date_to: customDateTo }));
    } else {
      dispatch(fetchCorporateAnalytics({ period }));
    }
  }, [period, customDateFrom, customDateTo, dispatch]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleCustomDateApply = () => {
    if (customDateFrom && customDateTo) {
      dispatch(fetchCorporateAnalytics({ date_from: customDateFrom, date_to: customDateTo }));
    }
  };

  if (isLoading && !analytics) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const activityBreakdownData = analytics?.activity_breakdown 
    ? Object.entries(analytics.activity_breakdown).map(([activity, data]) => ({
        name: activity,
        value: data.bookings,
        percentage: data.percentage
      }))
    : [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Corporate Analytics
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Track employee engagement and wellness metrics
        </Typography>
      </Box>

      {/* Period Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Period</InputLabel>
                <Select
                  value={period}
                  label="Period"
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <MenuItem value="month">Last Month</MenuItem>
                  <MenuItem value="quarter">Last Quarter</MenuItem>
                  <MenuItem value="year">Last Year</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {period === 'custom' && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="From"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="To"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleCustomDateApply}
                    sx={{ backgroundColor: '#14B8A6', height: '56px' }}
                  >
                    Apply
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {analytics && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PeopleIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Total Employees
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {analytics.summary.total_employees}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                    {analytics.summary.active_employees} active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MoneyIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Coins Loaded
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {analytics.summary.coins_loaded.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                    {analytics.summary.coins_used.toLocaleString()} used
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Utilization
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {analytics.summary.utilization_percentage}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                    Coin usage rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FitnessIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Total Bookings
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {analytics.summary.total_bookings}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                    {analytics.summary.avg_bookings_per_employee} avg/employee
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3}>
            {/* Activity Breakdown */}
            <Grid item xs={12} lg={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Activity Breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={activityBreakdownData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${entry.percentage}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {activityBreakdownData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Utilization Trend */}
            <Grid item xs={12} lg={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Utilization Trend
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={analytics.trends.utilization_trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="percentage" stroke="#14B8A6" strokeWidth={2} name="Utilization %" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Department Analysis */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Department Analysis
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.department_analysis}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="department" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="employees" fill="#14B8A6" name="Total Employees" />
                      <Bar dataKey="active_employees" fill="#10B981" name="Active Employees" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Health Metrics */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Health Metrics
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                          Avg Steps/Day
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#14B8A6' }}>
                          {analytics.health_metrics.avg_steps_per_day.toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                          Avg Workout (min)
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#F59E0B' }}>
                          {analytics.health_metrics.avg_workout_duration}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                          Avg Heart Rate
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#EF4444' }}>
                          {analytics.health_metrics.avg_heart_rate}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                          Avg Sleep (hrs)
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#8B5CF6' }}>
                          {analytics.health_metrics.avg_sleep_hours}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default CorporateAnalyticsPage;
